import { useEffect, useRef, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { Bot, Send, Loader2, MessageCircle, X, User, Headset, FileText } from "lucide-react";
import { toast } from "sonner";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const SESSION_KEY = "cr-rudi-session";
const OPEN_KEY = "cr-rudi-open";

const CHIPS = ["Ticket prices", "Birthday parties", "Free birthday ride", "Opening hours", "Directions"];
const WELCOME =
  "Hi, I'm Rudi — you get one free ride during your birthday month! Ask me anything about the park: tickets, prices, birthday parties, hours or the weather.";

function getSessionId() {
  let id = sessionStorage.getItem(SESSION_KEY);
  if (!id) {
    id = `rudi-${Math.random().toString(36).slice(2, 10)}${Date.now().toString(36)}`;
    sessionStorage.setItem(SESSION_KEY, id);
  }
  return id;
}

export default function AiChatWidget() {
  const [open, setOpen] = useState(() => sessionStorage.getItem(OPEN_KEY) === "1");
  const [messages, setMessages] = useState([{ role: "assistant", text: WELCOME }]);
  const [input, setInput] = useState("");
  const [busy, setBusy] = useState(false);
  const [showForm, setShowForm] = useState(false);
  const [form, setForm] = useState({ name: "", email: "", phone: "", message: "" });
  const [sending, setSending] = useState(false);
  const scrollRef = useRef(null);
  const sessionRef = useRef(getSessionId());
  const openedAt = useRef(Date.now());
  // the ticket flyer (front & back) rides along with the welcome message
  const [flyerSide, setFlyerSide] = useState("front");
  // the cookie banner hugs the bottom too — lift Rudi above it until the visitor chooses
  const [cookieDone, setCookieDone] = useState(() => !!localStorage.getItem("cr-popia-consent"));
  useEffect(() => {
    const t = setInterval(() => setCookieDone(!!localStorage.getItem("cr-popia-consent")), 800);
    return () => clearInterval(t);
  }, []);
  const lift = cookieDone ? "bottom-20 sm:bottom-24" : "bottom-48 sm:bottom-24";

  useEffect(() => {
    sessionStorage.setItem(OPEN_KEY, open ? "1" : "0");
  }, [open]);

  useEffect(() => {
    scrollRef.current?.scrollTo({ top: scrollRef.current.scrollHeight, behavior: "smooth" });
  }, [messages, busy]);

  const ask = async (text) => {
    const question = (text || "").trim();
    if (!question || busy) return;
    setInput("");
    setBusy(true);
    setMessages((m) => [...m, { role: "user", text: question }, { role: "assistant", text: "" }]);
    try {
      const res = await fetch(`${API}/ai/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ session_id: sessionRef.current, message: question }),
      });
      if (!res.ok || !res.body) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || "Rudi didn't catch that — please try again");
      }
      const reader = res.body.getReader();
      const dec = new TextDecoder();
      let buf = "";
      for (;;) {
        const { done, value } = await reader.read();
        if (done) break;
        buf += dec.decode(value, { stream: true });
        const parts = buf.split("\n\n");
        buf = parts.pop();
        for (const part of parts) {
          const line = part.split("\n").find((l) => l.startsWith("data:"));
          if (!line) continue;
          const payload = line.slice(5).trim();
          if (!payload || payload === "{}") continue;
          const obj = JSON.parse(payload);
          if (obj.t) {
            setMessages((m) => {
              const copy = [...m];
              copy[copy.length - 1] = { role: "assistant", text: copy[copy.length - 1].text + obj.t };
              return copy;
            });
          }
          if (obj.err) throw new Error(obj.err);
        }
      }
    } catch (e) {
      setMessages((m) => {
        const copy = [...m];
        copy[copy.length - 1] = { role: "assistant", text: e.message || "Rudi lost the thread — please ask again." };
        return copy;
      });
    } finally {
      setBusy(false);
    }
  };

  const sendToDesk = async (e) => {
    e.preventDefault();
    if (sending) return;
    setSending(true);
    try {
      const res = await fetch(`${API}/chat`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          session_id: sessionRef.current,
          ...form,
          website: "",
          elapsed_ms: Date.now() - openedAt.current,
        }),
      });
      const data = await res.json().catch(() => ({}));
      if (!res.ok) throw new Error(typeof data.detail === "string" ? data.detail : "Could not send");
      toast.success("The front desk got your message — we'll reply soon");
      setShowForm(false);
      setForm({ name: "", email: "", phone: "", message: "" });
    } catch (err) {
      toast.error(err.message || "Could not send — try again");
    } finally {
      setSending(false);
    }
  };

  return (
    <>
      {!open && (
        <motion.button
          initial={{ scale: 0.6, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          onClick={() => setOpen(true)}
          className={`fixed ${lift} right-3 z-[80] flex h-20 w-20 items-center justify-center rounded-full bg-[#f8b818] text-[#0a0a0b] shadow-[0_12px_34px_rgba(248,184,24,0.55)] transition-all duration-300 hover:scale-105 active:scale-95 sm:right-4 sm:h-auto sm:w-auto sm:py-4 sm:pl-4 sm:pr-7`}
          data-testid="ai-chat-trigger-button"
        >
          <span className="block text-base font-extrabold leading-tight sm:hidden" data-testid="ai-chat-trigger-word">Chat</span>
          <span className="hidden items-center gap-2.5 sm:flex">
            <span className="block text-base font-extrabold">Ask Rudi</span>
          </span>
        </motion.button>
      )}

      {open && (
        <motion.div
          initial={{ opacity: 0, y: 24 }}
          animate={{ opacity: 1, y: 0 }}
          className={`card-dark fixed ${showForm ? (cookieDone ? "bottom-28 sm:bottom-24" : "bottom-52 sm:bottom-24") : lift} left-2 right-2 z-[80] flex h-[min(72dvh,600px)] flex-col overflow-hidden sm:left-auto sm:w-[calc(100vw-2rem)] sm:max-w-sm`}
          data-testid="ai-chat-window"
        >
          <div className="flex items-center justify-between gap-3 border-b border-white/10 bg-[#141416] px-4 py-3">
            <div className="flex items-center gap-3">
              <span className="flex h-10 w-10 items-center justify-center rounded-full bg-[#f8b818]/15 text-[#f8b818]">
                <Bot size={20} />
              </span>
              <div>
                <p className="text-sm font-extrabold text-[#fff7e8]">Rudi — park assistant</p>
                <p className="text-[11px] font-bold uppercase tracking-widest text-[#4ade80]">Online · answers instantly</p>
              </div>
            </div>
            <button onClick={() => setOpen(false)} className="rounded-full border border-white/15 p-2.5 text-[#b9b4a6] hover:border-[#f8b818] hover:text-[#f8b818]" aria-label="Close chat" data-testid="ai-chat-close-btn">
              <X size={18} />
            </button>
          </div>

          <div ref={scrollRef} className="flex-1 space-y-3 overflow-y-auto px-4 py-4" data-testid="ai-chat-messages">
            {messages.map((m, i) => (
              <div key={i} className={`flex gap-2 ${m.role === "user" ? "justify-end" : ""}`}>
                {m.role === "assistant" && <Bot size={16} className="mt-1 shrink-0 text-[#f8b818]" />}
                <span
                  className={`max-w-[80%] whitespace-pre-wrap rounded-2xl px-3.5 py-2.5 text-sm leading-relaxed ${
                    m.role === "user" ? "bg-[#f8b818] font-bold text-[#0a0a0b]" : "bg-[#141416] text-[#fff7e8] border border-white/10"
                  }`}
                  data-testid={`ai-chat-msg-${m.role}`}
                >
                  {m.text || (busy && i === messages.length - 1 ? <Loader2 size={14} className="animate-spin" /> : "")}
                </span>
                {m.role === "user" && <User size={16} className="mt-1 shrink-0 text-[#b9b4a6]" />}
              </div>
            ))}

            {/* the park's ticket flyer — front & back, with a fast-path to more information */}
            {!messages.some((m) => m.role === "user") && (
              <div className="ml-7 overflow-hidden rounded-2xl border border-white/10 bg-[#141416]" data-testid="ai-chat-flyer-card">
                <a href={flyerSide === "front" ? "/flyer/flyer-front.png" : "/flyer/flyer-back.png"} target="_blank" rel="noopener noreferrer" title="Open the ticket flyer full size">
                  <img
                    src={flyerSide === "front" ? "/flyer/flyer-front.png" : "/flyer/flyer-back.png"}
                    alt={flyerSide === "front" ? "Cool Runnings ticket flyer — front: all ticket prices" : "Cool Runnings ticket flyer — back: map, directions and opening hours"}
                    className="w-full transition-transform duration-300 hover:scale-[1.02]"
                    data-testid="ai-chat-flyer-image"
                  />
                </a>
                <div className="space-y-2 p-3">
                  <span className="flex items-center gap-1.5 text-xs font-extrabold uppercase tracking-widest text-[#b9b4a6]">
                    <FileText size={13} className="text-[#f8b818]" /> Ticket flyer — tap to zoom
                  </span>
                  <div className="grid grid-cols-2 gap-2">
                    <button
                      onClick={() => setFlyerSide((s) => (s === "front" ? "back" : "front"))}
                      className="rounded-full border-2 border-white/20 px-3 py-3 text-xs font-extrabold uppercase tracking-wide text-[#fff7e8] transition-colors hover:border-[#f8b818] hover:text-[#f8b818]"
                      data-testid="ai-chat-flyer-flip-btn"
                    >
                      {flyerSide === "front" ? "See the back" : "See the front"}
                    </button>
                    <Link
                      to="/pricing"
                      onClick={() => setOpen(false)}
                      className="btn-gold justify-center !px-3 !py-3 !text-xs"
                      data-testid="ai-chat-flyer-more-btn"
                    >
                      More information
                    </Link>
                  </div>
                </div>
              </div>
            )}
          </div>

          <div className={`border-t border-white/10 px-4 pt-2 ${showForm ? "pb-8" : "pb-3"}`}>
            <div className="flex gap-2 overflow-x-auto pb-2 [scrollbar-width:none]">
              {CHIPS.map((c) => (
                <button
                  key={c}
                  onClick={() => ask(c)}
                  disabled={busy}
                  className="shrink-0 rounded-full border border-white/15 px-4 py-2.5 text-xs font-extrabold text-[#b9b4a6] transition-colors hover:border-[#f8b818] hover:text-[#f8b818] disabled:opacity-50"
                  data-testid={`ai-chat-chip-${c.toLowerCase().replace(/[^a-z]+/g, "-")}`}
                >
                  {c}
                </button>
              ))}
            </div>
            <form
              onSubmit={(e) => {
                e.preventDefault();
                ask(input);
              }}
              className="flex items-center gap-2"
            >
              <input
                value={input}
                onChange={(e) => setInput(e.target.value)}
                placeholder="Ask about tickets, parties, hours…"
                maxLength={1000}
                className="input-dark flex-1 !py-3.5 text-base"
                data-testid="ai-chat-input"
              />
              <button type="submit" disabled={busy || !input.trim()} className="btn-gold !p-4 disabled:opacity-50" aria-label="Send" data-testid="ai-chat-send-btn">
                {busy ? <Loader2 size={20} className="animate-spin" /> : <Send size={20} />}
              </button>
            </form>
            <button
              onClick={() => setShowForm(!showForm)}
              className="mt-1 flex items-center gap-1.5 py-2 text-[11px] font-extrabold uppercase tracking-widest text-[#b9b4a6] transition-colors hover:text-[#f8b818]"
              data-testid="ai-chat-human-fallback-btn"
            >
              <Headset size={12} /> Leave a message for the front desk
            </button>
            {showForm && (
              <form onSubmit={sendToDesk} className="mt-2 space-y-2 rounded-xl border border-white/10 bg-black/30 p-3" data-testid="ai-chat-desk-form">
                <p className="text-[11px] font-bold text-[#b9b4a6]">Leave your details and the front desk will get back to you.</p>
                <input required maxLength={80} placeholder="Your name" value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="input-dark !py-3 text-base" data-testid="desk-name-input" />
                <input required type="email" maxLength={120} placeholder="Email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} className="input-dark !py-3 text-base" data-testid="desk-email-input" />
                <input required type="tel" maxLength={20} placeholder="Phone number" value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} className="input-dark !py-3 text-base" data-testid="desk-phone-input" />
                <textarea required maxLength={1000} rows={2} placeholder="Your message" value={form.message} onChange={(e) => setForm({ ...form, message: e.target.value })} className="input-dark !py-3 text-base" data-testid="desk-message-input" />
                <button type="submit" disabled={sending} className="btn-gold w-full justify-center !py-3.5 !text-sm" data-testid="desk-send-btn">
                  {sending ? <Loader2 size={13} className="animate-spin" /> : <MessageCircle size={13} />} Send to front desk
                </button>
              </form>
            )}
          </div>
        </motion.div>
      )}
    </>
  );
}
